import * as React from 'react';
import { Dialog } from '../dialog/Dialog';
import { DialogBody } from '../dialog/DialogBody';
import { Button } from '../button/Button';
import { DialogFooter } from '../dialog/DialogFooter';
import { Card } from '../card/Card';
import { DynamicDateValue } from '@mc/data-filter-react';
import {
  computeExtendedFilter,
  ExtendedFilter,
  FilterDef,
  filtersGetInitialValue,
  filtersIsFieldValueFilled,
  getFilterComponent,
} from './Filters';
import { DYNAMIC_DATE_LABELS } from './FilterItem';
import { useState } from 'react';
import { ModalComponentProps } from '../modal/ModalManager';
import { CloseButton } from '../button/CloseButton';
import { List } from '../list/List';
import { ListItem } from '../list/ListItem';
import { Col, Row } from '../row';
import { FilterType } from './types';
import { FormRenderProps } from 'react-final-form';
import { Icon } from '../icon/Icon';
import {
  mcCheck,
  mcEquals,
  mcNoValueSpecified,
  mcSearch,
} from '@mc/react-ui-icons';
import { Text } from '../typography/Text';
import { Menu } from '../menu/Menu';
import { MenuItem } from '../menu/MenuItem';
import { MenuDivider } from '../menu/MenuDivider';
import { modalFormDialog } from '../form/utils';
import { OnChange } from 'react-final-form-listeners';
import { ButtonToolbar } from '../button-toolbar/ButtonToolbar';
import { ClearFilter } from './ClearFilter';
import { ClearFilterConfig } from '../rows-data/types';
import { SavedFilters } from './SavedFilters';
import { StringInput } from '../string/StringInput';
import { NoData } from '../no-data/NoData';
import { FormApi } from 'final-form';

type Props<FORM> = {
  filtersDef: FilterDef[];
  visibleFields?: string[];
  onChange: (formData: any, visibleFields: string[]) => void;
  getClearFilter: (config: ClearFilterConfig) => any;
  type: FilterType;
  formRenderProps: FormRenderProps<FORM>;
  visibleFilterProfiles: boolean;
  settingsKey: string;
  initialVisibleFields?: string[];
  /**
   * Extended nastavení filtru, např. pro přepínání mezi "je vyplněno" a "není vyplněno", nebo přepínání mezi "=" a "!=".
   * Lze zapnout globálně pro všechny položky na Filters a případně pro jednu položku zase vypnout.
   */
  extendedFilter: ExtendedFilter;
} & ModalComponentProps;

type SilentChange = {
  name: string;
  value: any;
};

export const ManageVisibleFieldsModal: React.FC<Props<any>> = ({
  filtersDef,
  visibleFields,
  initialVisibleFields,
  extendedFilter,
  onChange,
  formRenderProps,
  visibleFilterProfiles,
  settingsKey,
  getClearFilter,
  type,
  onClose,
}) => {
  const [search, setSearch] = React.useState('');
  let srchVal = search
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

  const computeCheckedFields = (visibleFields?: string[], formValues?: any) => {
    if (visibleFields) {
      return visibleFields;
    } else {
      // Pokud nejsou definována visibleFields, zobrazit jen filtry s hodnotami
      if (formValues) {
        const fieldsWithValues: string[] = [];
        filtersDef.forEach((filterDef) => {
          if (filtersIsFieldValueFilled(type, formValues, filterDef)) {
            fieldsWithValues.push(filterDef.name);
          }
        });
        return fieldsWithValues;
      }

      // Fallback - všechny filtry (např. při inicializaci bez hodnot)
      const vf = filtersDef.map((x) => x.name);
      return vf;
    }
  };

  const [checkedFields, setCheckedFields] = useState<string[]>(() => {
    return computeCheckedFields(visibleFields);
  });

  function silentChange(silentChanges: SilentChange[], form: FormApi<any>) {
    silentFieldChangeRef.current = true;
    try {
      form.batch(() => {
        for (const silentChange of silentChanges) {
          form.change(silentChange.name, silentChange.value);
        }
      });
    } finally {
      // vynuluj až po flushi notifikací
      Promise.resolve().then(() => {
        silentFieldChangeRef.current = false;
      });
    }
  }

  const onCheck = (filterDef: FilterDef) => {
    let newCheckedFields = [...checkedFields];
    if (newCheckedFields.indexOf(filterDef.name) >= 0) {
      newCheckedFields = newCheckedFields.filter((x) => x !== filterDef.name);
      setCheckedFields(newCheckedFields);

      // Vymazání hodnoty filtru pokud uživatel zruší viditelnost daného filtru
      const newValue = filtersGetInitialValue(filterDef, type);
      silentChange(
        [{ name: filterDef.name, value: newValue }],
        formRenderProps.form
      );
    } else {
      newCheckedFields.push(filterDef.name);
      setCheckedFields(newCheckedFields);
    }
  };

  const silentFieldChangeRef = React.useRef(false);

  const onAutoCheck = (name: string, value: any) => {
    const filterDef = filtersDef.find((f) => f.name === name);
    if (!filterDef) {
      return;
    }
    const hasValue = filtersIsFieldValueFilled(
      type,
      { [name]: value },
      filterDef
    );
    if (!hasValue) {
      return;
    }

    setCheckedFields((fields) => {
      let newCheckedFields = [...fields];
      if (newCheckedFields.indexOf(name) < 0) {
        newCheckedFields.push(name);
      }
      return newCheckedFields;
    });
  };

  const renderFilterByValueIcon = (
    extendedFilterConf: ExtendedFilter,
    filterType: FilterType,
    formFieldValue: any,
    onChangeFormField: (value: any) => void
  ) => {
    if (
      extendedFilterConf.filterByFilled ||
      extendedFilterConf.filterByNot ||
      extendedFilterConf.filterByDynamicDate
    ) {
      const isEmpty =
        filterType === 'default' ? !!formFieldValue?.emptyValue : false;
      const isNotEmpty =
        filterType === 'default' ? !!formFieldValue?.notEmptyValue : false;
      const isNot =
        filterType === 'default' ? !!formFieldValue?.notValue : false;
      const dynamicDateValue: DynamicDateValue | undefined =
        filterType === 'default'
          ? formFieldValue?.dynamicDateValue
          : undefined;

      let content;
      if (isEmpty) {
        content = <Text type={'value'}>Nevyplněno</Text>;
      } else if (isNotEmpty) {
        content = <Text type={'value'}>Vyplněno</Text>;
      } else if (dynamicDateValue) {
        content = (
          <Text type={'value'}>{DYNAMIC_DATE_LABELS[dynamicDateValue]}</Text>
        );
      } else if (isNot) {
        content = (
          <Icon fixedWidth icon={mcNoValueSpecified} intent={'danger'} />
        );
      } else {
        content = <Icon fixedWidth icon={mcEquals} />;
      }

      const handleFilterByIsEmpty = () => {
        if (filterType === 'default') {
          onChangeFormField({
            value: undefined,
            emptyValue: true,
            notEmptyValue: false,
            notValue: false,
          });
        }
      };

      const handleFilterByIsNotEmpty = () => {
        if (filterType === 'default') {
          onChangeFormField({
            value: undefined,
            emptyValue: false,
            notEmptyValue: true,
            notValue: false,
          });
        }
      };

      const handleFilterByNotValue = () => {
        if (filterType === 'default') {
          onChangeFormField({
            value: formFieldValue?.value,
            emptyValue: false,
            notEmptyValue: false,
            notValue: true,
          });
        }
      };

      const handleFilterByValue = () => {
        if (filterType === 'default') {
          onChangeFormField({
            value: formFieldValue?.value,
            emptyValue: false,
            notEmptyValue: false,
            notValue: false,
          });
        }
      };

      const handleFilterByDynamicDate = (value: DynamicDateValue) => {
        if (filterType === 'default') {
          onChangeFormField({
            value: undefined,
            emptyValue: false,
            notEmptyValue: false,
            notValue: false,
            dynamicDateValue: value,
          });
        }
      };

      const DYNAMIC_DATE_PRIMARY: DynamicDateValue[] = [
        DynamicDateValue.YESTERDAY,
        DynamicDateValue.TODAY,
        DynamicDateValue.TOMORROW,
        DynamicDateValue.LTE_TODAY,
        DynamicDateValue.GTE_TODAY,
      ];

      const DYNAMIC_DATE_MORE: DynamicDateValue[] = [
        DynamicDateValue.LT_TODAY,
        DynamicDateValue.GT_TODAY,
        DynamicDateValue.LAST_7_DAYS,
        DynamicDateValue.NEXT_7_DAYS,
        DynamicDateValue.LAST_WEEK,
        DynamicDateValue.THIS_WEEK,
        DynamicDateValue.NEXT_WEEK,
        DynamicDateValue.LAST_MONTH,
        DynamicDateValue.THIS_MONTH,
        DynamicDateValue.NEXT_MONTH,
        DynamicDateValue.LAST_QUARTER,
        DynamicDateValue.THIS_QUARTER,
        DynamicDateValue.NEXT_QUARTER,
        DynamicDateValue.LAST_HALF_YEAR,
        DynamicDateValue.THIS_HALF_YEAR,
        DynamicDateValue.NEXT_HALF_YEAR,
        DynamicDateValue.LAST_YEAR,
        DynamicDateValue.THIS_YEAR,
        DynamicDateValue.NEXT_YEAR,
      ];

      return (
        <Button
          style={'link'}
          className={'px-2'}
          menu={
            <Menu>
              <MenuItem
                icon={
                  !isNotEmpty && !isEmpty && !isNot && !dynamicDateValue ? (
                    <Icon icon={mcCheck} />
                  ) : undefined
                }
                text={'Zadaná hodnota'}
                onClick={() => {
                  handleFilterByValue();
                }}
              />
              {extendedFilterConf?.filterByNot && (
                <MenuItem
                  text={'Nerovná se zadané hodnotě'}
                  icon={isNot ? <Icon icon={mcCheck} /> : undefined}
                  onClick={() => {
                    handleFilterByNotValue();
                  }}
                />
              )}
              {extendedFilterConf?.filterByFilled && (
                <>
                  <MenuDivider />
                  <MenuItem
                    text={'Vyplněno'}
                    icon={isNotEmpty ? <Icon icon={mcCheck} /> : undefined}
                    onClick={() => {
                      handleFilterByIsNotEmpty();
                    }}
                  />
                  <MenuItem
                    text={'Nevyplněno'}
                    icon={isEmpty ? <Icon icon={mcCheck} /> : undefined}
                    onClick={() => {
                      handleFilterByIsEmpty();
                    }}
                  />
                </>
              )}
              {extendedFilterConf?.filterByDynamicDate && (
                <>
                  <MenuDivider />
                  {DYNAMIC_DATE_PRIMARY.map((val) => (
                    <MenuItem
                      key={val}
                      text={DYNAMIC_DATE_LABELS[val]}
                      icon={
                        dynamicDateValue === val ? (
                          <Icon icon={mcCheck} />
                        ) : undefined
                      }
                      onClick={() => {
                        handleFilterByDynamicDate(val);
                      }}
                    />
                  ))}
                  <MenuItem text={'Další hodnoty'}>
                    {DYNAMIC_DATE_MORE.map((val) => (
                      <MenuItem
                        key={val}
                        text={DYNAMIC_DATE_LABELS[val]}
                        icon={
                          dynamicDateValue === val ? (
                            <Icon icon={mcCheck} />
                          ) : undefined
                        }
                        onClick={() => {
                          handleFilterByDynamicDate(val);
                        }}
                      />
                    ))}
                  </MenuItem>
                </>
              )}
            </Menu>
          }
        >
          {content}
        </Button>
      );
    } else {
      return <Icon icon={mcEquals} />;
    }
  };

  const shouldRenderItemInput = (
    extendedFilterConf: ExtendedFilter,
    filterDef: FilterDef
  ) => {
    const formFieldValue = formRenderProps.values?.[filterDef.name];
    if (
      extendedFilterConf.filterByFilled ||
      extendedFilterConf.filterByNot ||
      extendedFilterConf.filterByDynamicDate
    ) {
      const isEmpty = type === 'default' ? formFieldValue?.emptyValue!! : false;
      const isNotEmpty =
        type === 'default' ? formFieldValue?.notEmptyValue!! : false;
      const dynamicDateValue =
        type === 'default' ? formFieldValue?.dynamicDateValue : undefined;

      if (isEmpty || isNotEmpty || dynamicDateValue) {
        return false;
      } else {
        return true;
      }
    }
    return true;
  };

  const filteredFiltersDef = filtersDef.filter((filter) => {
    if (!srchVal || srchVal.length === 0) {
      return true;
    }
    const label = filter.label
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '');
    return label.indexOf(srchVal) >= 0;
  });

  const selectAll = () => {
    let newCheckedFields = [...checkedFields];

    filteredFiltersDef.forEach((filterDef) => {
      if (newCheckedFields.indexOf(filterDef.name) === -1) {
        newCheckedFields.push(filterDef.name);
      }
    });

    setCheckedFields(newCheckedFields);
  };

  const unSelectAll = () => {
    let newCheckedFields = [...checkedFields];

    const silentChanges: SilentChange[] = [];

    filteredFiltersDef.forEach((filterDef) => {
      if (newCheckedFields.indexOf(filterDef.name) >= 0) {
        newCheckedFields = newCheckedFields.filter((x) => x !== filterDef.name);

        // Vymazání hodnoty filtru pokud uživatel zruší viditelnost daného filtru
        const newValue = filtersGetInitialValue(filterDef, type);
        silentChanges.push({ name: filterDef.name, value: newValue });
      }
    });

    setCheckedFields(newCheckedFields);

    // Vymazání hodnoty filtru pokud uživatel zruší viditelnost daného filtru
    if (silentChanges.length > 0) {
      silentChange(silentChanges, formRenderProps.form);
    }
  };

  const realSelectedItems = React.useMemo(
    () =>
      filteredFiltersDef.filter((filter) =>
        checkedFields.includes(filter.name)
      ),
    [srchVal, filtersDef, checkedFields]
  );
  const realUnselectedItems = React.useMemo(
    () =>
      filteredFiltersDef.filter(
        (filter) => !checkedFields.includes(filter.name)
      ),
    [srchVal, filtersDef, checkedFields]
  );

  return (
    <Dialog
      autoFocus={false}
      showOnTop
      size={'xl'}
      onClose={onClose}
      title={'Výběr položek filtru'}
    >
      <DialogBody>
        <Card mode={'edit'}>
          <List>
            <ListItem disableHover>
              <Row noGutters>
                <Col>
                  <StringInput
                    size={32}
                    placeholder="Vyhledat"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setSearch(e.target.value);
                    }}
                    rightElement={<Icon fixedWidth icon={mcSearch} />}
                  />
                </Col>
              </Row>
              <Row noGutters className={'mt-2'}>
                <Col>
                  <Button
                    style={'link'}
                    onClick={selectAll}
                    disabled={
                      realSelectedItems.length === filteredFiltersDef.length
                    }
                  >
                    Vybrat vše
                  </Button>
                </Col>
                <Col className={'d-flex justify-content-end'}>
                  <Button
                    style={'link'}
                    onClick={unSelectAll}
                    disabled={
                      realUnselectedItems.length === filteredFiltersDef.length
                    }
                  >
                    Odebrat vše
                  </Button>
                </Col>
              </Row>
            </ListItem>
            {filteredFiltersDef.length === 0 && <NoData />}
            {filteredFiltersDef.map((filter) => {
              const component = getFilterComponent(
                filter,
                type,
                extendedFilter,
                {
                  noLabel: true,
                }
              );

              const extendedFilterConf = computeExtendedFilter(
                filter,
                extendedFilter
              );

              let res;
              if (shouldRenderItemInput(extendedFilterConf, filter)) {
                res = component({
                  filterDef: filter,
                  onSearch: () => {},
                });
              }

              let checked = checkedFields.includes(filter.name);
              return (
                <ListItem
                  checkable
                  noCheckedHighlight
                  checked={checked}
                  onCheck={() => onCheck(filter)}
                >
                  <Row>
                    <Col xs={5} className={'d-flex align-items-center'}>
                      <Text
                        type={'value'}
                        intent={checked ? undefined : 'inactive'}
                      >
                        {filter.label}
                      </Text>
                    </Col>
                    <Col
                      xs={'auto'}
                      style={{ width: 120 }}
                      className={'d-flex align-items-center'}
                    >
                      {renderFilterByValueIcon(
                        extendedFilterConf,
                        type,
                        formRenderProps.values?.[filter.name],
                        (value) =>
                          formRenderProps.form.change(filter.name, value)
                      )}
                    </Col>
                    <Col xs={'auto'} className={'d-flex align-items-center'}>
                      <React.Fragment>{res}</React.Fragment>
                      <OnChange name={filter.name}>
                        {(value) => {
                          if (!silentFieldChangeRef.current) {
                            onAutoCheck(filter.name, value);
                          }
                        }}
                      </OnChange>
                    </Col>
                  </Row>
                </ListItem>
              );
            })}
          </List>
        </Card>
      </DialogBody>
      <DialogFooter
        extra={
          <ButtonToolbar>
            {visibleFilterProfiles && settingsKey && (
              <SavedFilters
                buttonText={'Uložené filtry'}
                filtersDef={filtersDef}
                type={type}
                settingsKey={settingsKey}
                formData={formRenderProps.values}
                visibleFields={checkedFields}
                onSetFilter={(formData, _, visibleFields) => {
                  formRenderProps.form.reset(formData);
                  setCheckedFields(computeCheckedFields(visibleFields, formData));
                }}
              />
            )}
            <ClearFilter
              type={type}
              filtersDef={filtersDef}
              getClearFilter={getClearFilter}
              setNewFilter={formRenderProps.form.reset}
              initialVisibleFields={initialVisibleFields}
              setVisibleFields={(visibleFields) =>
                setCheckedFields(computeCheckedFields(visibleFields))
              }
            />
          </ButtonToolbar>
        }
      >
        <CloseButton onClick={onClose}>Zrušit</CloseButton>
        <Button
          style={'primary'}
          dataTest={'submit'}
          onClick={() => {
            onChange(formRenderProps.values, checkedFields);
          }}
        >
          Vyhledat
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

export const ManageVisibleFieldsModalForm =
  // @ts-ignore
  modalFormDialog<any, Props>(ManageVisibleFieldsModal, {});
